import { logger } from '@/ui'
import { axiosInstance } from '@jobilla/nexus-sdk'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

/**
 * Lazily-initialised Laravel Echo instance bound to the local Reverb daemon
 * (or the configured one in non-dev environments). Private channels are
 * authorised via the Nexus API, reusing the WorkOS bearer that the axios
 * instance is already injecting on every request.
 */
let echoInstance: Echo<'reverb'> | null = null

type EchoEnv = {
	key: string
	host: string
	port: number
	scheme: 'http' | 'https'
}

function readEnv(): EchoEnv {
	const env = import.meta.env as Record<string, string | undefined>
	const rawScheme = env.VITE_REVERB_SCHEME
	const scheme: 'http' | 'https' = rawScheme === 'https' || rawScheme === 'http' ? rawScheme : 'http'
	const parsedPort = Number.parseInt(env.VITE_REVERB_PORT ?? '', 10)
	const port = Number.isInteger(parsedPort) && parsedPort >= 1 && parsedPort <= 65535 ? parsedPort : 8080
	return {
		key: env.VITE_REVERB_APP_KEY ?? 'dev-reverb-key',
		host: env.VITE_REVERB_HOST ?? 'localhost',
		port,
		scheme,
	}
}

export function getEcho(getAccessToken: () => Promise<string | null>): Echo<'reverb'> {
	if (echoInstance) {
		return echoInstance
	}

	const cfg = readEnv()
	const baseURL = axiosInstance.defaults.baseURL ?? ''

	echoInstance = new Echo({
		broadcaster: 'reverb',
		Pusher,
		key: cfg.key,
		wsHost: cfg.host,
		wsPort: cfg.port,
		wssPort: cfg.port,
		forceTLS: cfg.scheme === 'https',
		enabledTransports: ['ws', 'wss'],
		authorizer: ((channel: { name: string }) => ({
			authorize: (socketId: string, callback: (error: Error | null, data: unknown) => void): void => {
				void (async () => {
					try {
						const token = await getAccessToken()
						const response = await fetch(`${baseURL}/broadcasting/auth`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/x-www-form-urlencoded',
								'Accept': 'application/json',
								...(token ? { Authorization: `Bearer ${token}` } : {}),
							},
							body: new URLSearchParams({
								socket_id: socketId,
								channel_name: channel.name,
							}).toString(),
						})
						if (!response.ok) {
							throw new Error(`Broadcasting auth failed: ${response.status}`)
						}
						const data = await response.json()
						callback(null, data)
					} catch (error) {
						const err = error instanceof Error ? error : new Error(String(error))
						logger.error(err, {
							data: {
								scope: 'Echo',
								message: 'Channel auth failure',
								channel: channel.name,
								socket_id: socketId,
							},
						})
						callback(err, null)
					}
				})()
			},
		})) as never,
	})

	return echoInstance
}

export function teardownEcho(): void {
	echoInstance?.disconnect()
	echoInstance = null
}

/** Returns the current Echo singleton without creating one — for diagnostics only. */
export function peekEcho(): Echo<'reverb'> | null {
	return echoInstance
}

/** The resolved Reverb endpoint configuration — for diagnostics only. */
export function getEchoConfig(): EchoEnv {
	return readEnv()
}

export { type EchoConnectionState, useEchoConnectionState } from './use-connection-state'
export { usePrivateChannel } from './use-private-channel'
